(() => {
  function isWebGLSupported() {
    try {
      const canvas = document.createElement('canvas');
      return (
        !!window.WebGLRenderingContext
        && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
      );
    } catch (e) {
      return false;
    }
  }

  function createGradientBackground(container) {
    if (container === null) return;

    // TODO
    if (!isWebGLSupported()) {
      return;
    }

    const THREE = window.THREE;

    const shader = `
    uniform float time;
    uniform vec2 resolution;
  
    /* Colors */
    // Black BKG
    vec3 colorStop0 = vec3(0.039, 0.062, 0.071);
    // First pair, #F89638 and #FEBE73
    vec3 colorStop1a = vec3(0.973, 0.588, 0.220);
    vec3 colorStop1b = vec3(0.996, 0.745, 0.451);
    // Second pair, #BF3D51 and #EF6748
    vec3 colorStop2a = vec3(0.749, 0.239, 0.318);
    vec3 colorStop2b = vec3(0.937, 0.404, 0.282);
  
    /* Tuning parameters */
    // Factor multiplied to time value
    float colorFadeFactor = 0.0005;
    float zOffsetFactor = 0.000015;
    float resolutionScale = 2.5;
  
    vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
    vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}
  
    float simplexNoise(vec3 v){ 
      const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
      const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);
    
      // First corner
      vec3 i  = floor(v + dot(v, C.yyy) );
      vec3 x0 =   v - i + dot(i, C.xxx) ;
    
      // Other corners
      vec3 g = step(x0.yzx, x0.xyz);
      vec3 l = 1.0 - g;
      vec3 i1 = min( g.xyz, l.zxy );
      vec3 i2 = max( g.xyz, l.zxy );
    
      //  x0 = x0 - 0. + 0.0 * C 
      vec3 x1 = x0 - i1 + 1.0 * C.xxx;
      vec3 x2 = x0 - i2 + 2.0 * C.xxx;
      vec3 x3 = x0 - 1. + 3.0 * C.xxx;
    
      // Permutations
      i = mod(i, 289.0 ); 
      vec4 p = permute( permute( permute( 
                 i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
               + i.y + vec4(0.0, i1.y, i2.y, 1.0 )) 
               + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));
    
      // Gradients
      // ( N*N points uniformly over a square, mapped onto an octahedron.)
      float n_ = 1.0/7.0; // N=7
      vec3  ns = n_ * D.wyz - D.xzx;
    
      vec4 j = p - 49.0 * floor(p * ns.z *ns.z);  //  mod(p,N*N)
    
      vec4 x_ = floor(j * ns.z);
      vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)
    
      vec4 x = x_ *ns.x + ns.yyyy;
      vec4 y = y_ *ns.x + ns.yyyy;
      vec4 h = 1.0 - abs(x) - abs(y);
    
      vec4 b0 = vec4( x.xy, y.xy );
      vec4 b1 = vec4( x.zw, y.zw );
    
      vec4 s0 = floor(b0)*2.0 + 1.0;
      vec4 s1 = floor(b1)*2.0 + 1.0;
      vec4 sh = -step(h, vec4(0.0));
    
      vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
      vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;
    
      vec3 p0 = vec3(a0.xy,h.x);
      vec3 p1 = vec3(a0.zw,h.y);
      vec3 p2 = vec3(a1.xy,h.z);
      vec3 p3 = vec3(a1.zw,h.w);
      
      //Normalise gradients
      vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
      p0 *= norm.x;
      p1 *= norm.y;
      p2 *= norm.z;
      p3 *= norm.w;
    
      // Mix final noise value
      vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
      m = m * m;
      return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3) ) );
    }
  
    void main() {
      vec2 pos = gl_FragCoord.xy/resolution.xy;
  
      // Use several values to prevent it from looking too dark
      float value1 = sqrt(clamp(simplexNoise(vec3(pos.x * resolutionScale, pos.y * resolutionScale, time * zOffsetFactor)), 0.0, 1.0));
      float value2 = sqrt(clamp(simplexNoise(vec3(pos.x * resolutionScale, pos.y * resolutionScale, time * zOffsetFactor + 20.0)), 0.0, 1.0));
      float value3 = clamp(simplexNoise(vec3(pos.x * resolutionScale, pos.y * resolutionScale, time * zOffsetFactor +  27.0)), 0.0, 1.0);
      float value4 = clamp(simplexNoise(vec3(pos.x * resolutionScale, pos.y * resolutionScale, time * zOffsetFactor +  -12.0)), 0.0, 1.0);
  
      float value = mix(clamp(value1 + value3, 0.0, 1.0), clamp(value2 + value4, 0.0, 1.0) / 2.0, 0.5);
  
      float blendFactor = 0.5 * (sin(time * colorFadeFactor) + 1.0);
      vec3 interpolatedColorStopA = mix(colorStop1a, colorStop2a, blendFactor);
      vec3 interpolatedColorStopB = mix(colorStop1b, colorStop2b, blendFactor);
  
      float lowPortion = 2.0 * value;
      float highPortion = 2.0 * (value - 0.5);
      
      vec3 color1 = mix(colorStop0, interpolatedColorStopA, lowPortion);
      vec3 color2 = mix(interpolatedColorStopA, interpolatedColorStopB, highPortion);
      vec3 color = value > 0.5 ? color2 : color1;
  
      gl_FragColor = vec4(color,1.0);
    }
    `;

    const uniforms = {
      time: { type: 'f', value: 0.0 },
      resolution: {
        type: 'v2',
        value: new THREE.Vector2(),
      },
    };

    const material = new THREE.ShaderMaterial({
      uniforms,
      fragmentShader: shader,
    });

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(0.5, 1, 0.5, 0);
    camera.position.z = 1;
    scene.add(camera);

    // Triangular view plane
    const texturePlaneTriangle = new window.THREE.Shape([
      new THREE.Vector2(0, 0),
      new THREE.Vector2(1, 0),
      new THREE.Vector2(1, 1),
    ]);
    const texturePlaneGeometry = new THREE.ShapeGeometry(texturePlaneTriangle);
    const texturePlane = new THREE.Mesh(texturePlaneGeometry, material);
    scene.add(texturePlane);

    const renderer = new THREE.WebGLRenderer();
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1));
    const canvas = renderer.domElement;
    canvas.style.zIndex = 5;
    container.appendChild(canvas);
    let containerMax = 0;

    function resizeBackground() {
      const width = container.clientWidth;
      const height = container.clientHeight;
      containerMax = Math.max(width, height);
      renderer.setSize(containerMax, containerMax);
      renderer.render(scene, camera);
    }

    function animate(time) {
      uniforms.time.value = time;
      uniforms.resolution.value.x = containerMax;
      uniforms.resolution.value.y = containerMax;
      renderer.render(scene, camera);
      requestAnimationFrame(animate);
    }
    requestAnimationFrame(animate);

    window.addEventListener('resize', resizeBackground);
    resizeBackground();
  }

  function setupSlides() {
    const mm = gsap.matchMedia();
    const Draggable = window.Draggable;
    const InertiaPlugin = window.InertiaPlugin;

    gsap.registerPlugin(Draggable, InertiaPlugin);

    const slidesContainer = document.getElementById('slides');
    const slides = gsap.utils.toArray('.ffest__slide');

    mm.add('(max-width: 639px)', () => {
      mobileSlides();
    });
    mm.add('(max-height: 599px) and (max-width: 1023px) and (orientation: landscape)', () => {
      mobileSlides();
    });

    function mobileSlides() {
      document.querySelector('.ffest-navigation__container').style.display = 'none';
      slidesContainer.classList.add('no-animation');

      slides.forEach((slide) => {
        slide.classList.add('no-animation');

        gsap.set(slide, {
          yPercent: 0,
          opacity: 1,
        });
      });
    }

    mm.add('(min-width: 640px) and (min-height: 600px)', () => {
      desktopSlides();
    });

    function desktopSlides() {
      document.querySelector('.ffest-navigation__container').style.display = 'block';
      slidesContainer.classList.remove('no-animation');

      slides.forEach((slide, index) => {
        slide.classList.remove('no-animation');

        gsap.set(slide, {
          yPercent: index * 100,
          opacity: 1,
          // remove stacked slides upon load
        });
      });

      const slideDuration = 0.3;
      const numSlides = slides.length;
      const progressPerItem = 1 / (numSlides - 1);
      const snapProgress = gsap.utils.snap(progressPerItem);
      const snapProgressDirectional = (value, direction) => {
        const snapped = snapProgress(value);
        return (snapped - value < 0) === direction < 0
          ? snapped : snapProgress(direction < 0
            ? value - progressPerItem : value + progressPerItem);
      };
      let slideHeight;
      let totalHeight;
      const animation = gsap.to(slides, {
        yPercent: `-= ${((numSlides - 1) * 100)}`,
        duration: 1,
        ease: 'none',
        paused: true,
      });
      // tracks velocity of progress
      const tracker = InertiaPlugin.track(animation, 'progress')[0];
      // use a proxy element
      const draggable = new Draggable(document.createElement('div'), {
        trigger: '#slides',
        dragClickables: false,
        onPress() {
          gsap.killTweensOf(animation);
          this.startProgress = animation.progress();
        },
        onDrag() {
          const change = (draggable.startY - draggable.y) / totalHeight;
          animation.progress(draggable.startProgress + change);
        },
        onRelease() {
          const velocity = tracker.get('progress');
          gsap.to(animation, {
            inertia: {
              duration: { min: 0.5, max: 2 },
              progress: {
                velocity,
                min: 0,
                max: 1,
                end: (value) => snapProgressDirectional(value, velocity),
              },
            },
            overwrite: true,
          });
        },
      });

      function animateSlides(direction) {
        const progress = snapProgress(animation.progress() + direction * progressPerItem);
        if (progress >= 0 && progress <= 1) {
          gsap.to(animation, {
            progress,
            duration: slideDuration,
            overwrite: true,
          });
        }
      }

      function resize() {
        slideHeight = slides[0].offsetHeight;
        totalHeight = slideHeight * numSlides;
      }

      resize();

      window.addEventListener('resize', resize);
      document.querySelector('#previousButton').addEventListener('click', () => animateSlides(-1));
      document.querySelector('#nextButton').addEventListener('click', () => animateSlides(1));
      document.querySelectorAll('.ffest__slide-continue-button')
        .forEach((button) => button.addEventListener('click', () => animateSlides(1)));
    }
  }

  function videosLightbox() {
    window.MicroModal.init({
      openClass: 'is-open',
      awaitOpenAnimation: true,
      awaitCloseAnimation: true,
      disableScroll: true,
      onClose: (modal) => modal.querySelector('video').pause(),
    });
  }

  window.addEventListener('load', () => {
    const backgroundContainer = document.getElementById('slides');
    createGradientBackground(backgroundContainer);
    setupSlides();
    videosLightbox();
  });
})();
